import config from "../../config/config";


export const login = async (email, password) => {
  try {
    // console.log("loging using url: ",`${config.baseAuthUrl}login`);
    const response = await fetch(`${config.baseAuthUrl}login`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email, password }),
    });

    if (!response.ok) {
      throw new Error("Network response was not ok.");
    }

    const data = await response.json();

    if (data.token) {
      localStorage.setItem("token", JSON.stringify(data.token));
    }

    return data;
  } catch (error) {
    console.error("Login failed:", error);
    throw error;
  }
};

export const isAuthenticated = () => {
  const token = localStorage.getItem("token");
  return !!token;
};

export const getUserDetails = async () => {
  try {
    const token = JSON.parse(localStorage.getItem("token"));
    // console.log("Retrieved token:", token);

    const response = await fetch(`${config.baseAuthUrl}details`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    // console.log("getUserDetails response: ", response);

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error("Unauthorized access - please log in.");
      } else {
        throw new Error("Failed to fetch user details.");
      }
    }

    const data = await response.json();
    // console.log("User details fetched: ", data);

    // Store customerId locally for use in subsequent API calls
    localStorage.setItem("customerId", data.customerId);

    return data;
  } catch (error) {
    console.error("Error fetching user details:", error);
    throw error;
  }
};

export const logout = () => {
  localStorage.removeItem("token");

  // Dispatch a custom event to notify React state
  window.dispatchEvent(new Event("authChange"));
};


export const getAllUsers = async (token) => {
  try {
    const cleanToken = token?.replace(/^"|"$/g, "").trim();

    const response = await fetch(`${config.baseAuthUrl}all-users`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${cleanToken}`,
      },
    });

    if (!response.ok) {
      throw new Error("Failed to fetch users");
    }

    const data = await response.json();

    // ✅ Filter only users with no roles or an empty array
    const usersWithoutRoles = data.filter((user) => !user.roles || user.roles.length === 0);


    return usersWithoutRoles;
  } catch (error) {
    console.error("Error fetching users:", error);
    throw error;
  }
};

export const getAllBlockedUsers = async (token) => {
  try {
    const cleanToken = token?.replace(/^"|"$/g, "").trim();

    const response = await fetch(`${config.baseAuthUrl}blocked-users-with-events`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${cleanToken}`,
      },
    });

    if (!response.ok) {
      throw new Error("Failed to fetch users");
    }

    const data = await response.json();

    return data;
  } catch (error) {
    console.error("Error fetching users:", error);
    throw error;
  }
};



export const changePassword = async (oldPassword, newPassword) => {
  const token = JSON.parse(localStorage.getItem("token"));

  const response = await fetch(`${config.baseAuthUrl}change-password`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ oldPassword, newPassword }),
  });

  if (!response.ok) {
    throw new Error("Failed to change password.");
  }

  return response.json();
};

export const createUser = async (userData) => {

  try {
    const token = JSON.parse(localStorage.getItem("token"));
    const response = await fetch(`${config.baseAuthUrl}register-admin`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(userData),
    });

    if (!response.ok) {
      throw new Error("Failed to create user.");
    }

    return await response.json();
  } catch (error) {
    console.error("Error creating user:", error);
    throw error;
  }
};

export const createUserExisting = async (userData) => {

  try {
    const token = JSON.parse(localStorage.getItem("token"));
    const response = await fetch(`${config.baseAuthUrl}register-admin-existing`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(userData),
    });

    if (!response.ok) {
      // Try to extract detailed error message from response
      let errorMessage = "Failed to create user.";

      try {
        const errorText = await response.text();
        if (errorText) {
          try {
            const errorJson = JSON.parse(errorText);

            // Handle array of error strings
            if (Array.isArray(errorJson)) {
              errorMessage = errorJson.join("\n");
            }
            // Handle object with errors array
            else if (errorJson.errors && Array.isArray(errorJson.errors)) {
              errorMessage = errorJson.errors.join("\n");
            }
            // Handle object with title, detail, or message
            else if (errorJson.title || errorJson.detail || errorJson.message || errorJson.Message) {
              errorMessage = errorJson.title || errorJson.detail || errorJson.message || errorJson.Message;
            }
            // Handle plain error string
            else if (typeof errorJson === 'string') {
              errorMessage = errorJson;
            }
          } catch {
            // If JSON parsing fails, use raw text
            errorMessage = errorText;
          }
        }
      } catch {
        // If reading response fails, use status-based message
        if (response.status === 401) {
          errorMessage = "Unauthorized - Customer ID is invalid or missing.";
        } else if (response.status === 400) {
          errorMessage = "Bad request - please check the form data.";
        }
      }

      const error = new Error(errorMessage);
      error.status = response.status;
      throw error;
    }

    return await response.json();
  } catch (error) {
    console.error("Error creating user:", error);
    throw error;
  }
};

export const getRoles = async () => {
  try {
    const response = await fetch(`${config.baseAuthUrl}roles`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });

    if (!response.ok) {
      throw new Error("Failed to fetch roles");
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching roles:", error);
    throw error;
  }
};

export const resetPassword = async ({ email, token, newPassword }) => {
  // Build payloads with and without PascalCase to handle potential DTO casing requirements
  const payloadLower = {
    token,
    newPassword,
  };
  if (email) payloadLower.email = email;

  const payloadUpper = {
    Token: token,
    NewPassword: newPassword,
  };
  if (email) payloadUpper.Email = email;

  const url = `${config.baseAuthUrl}reset-password`;

  const tryRequest = async (body) => {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      // Capture server response for better diagnostics
      let errorText = "";
      try {
        errorText = await response.text();
      } catch { }

      let message = errorText || "Failed to reset password.";
      // Try to parse structured JSON error response
      try {
        const parsed = JSON.parse(errorText);
        if (parsed) {
          if (Array.isArray(parsed.errors)) {
            message = parsed.errors.join("\n");
          } else if (parsed.error) {
            message = parsed.error;
          } else if (parsed.title || parsed.detail || parsed.message) {
            message = parsed.title || parsed.detail || parsed.message;
          }
        }
      } catch { }

      const err = new Error(message);
      err.status = response.status;
      err.details = errorText;
      throw err;
    }

    return await response.json();
  };

  // First try with common lowercase keys
  try {
    return await tryRequest(payloadLower);
  } catch (err) {
    // If validation/format issue, retry with PascalCase which some DTOs require
    if (err && typeof err.status === "number" && [400, 415, 422].includes(err.status)) {
      return await tryRequest(payloadUpper);
    }
    throw err;
  }
};

export const changePasswordLoggedIn = async (token, oldPassword, newPassword) => {
  const response = await fetch(`${config.baseAuthUrl}change-password-loggedin`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      OldPassword: oldPassword,  // Must match DTO casing
      NewPassword: newPassword,
    }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    try {
      const errorJson = JSON.parse(errorText);
      throw new Error(errorJson?.title || "Failed to change password.");
    } catch {
      throw new Error(errorText);
    }
  }

  return await response.json();
};



export const sendForgotPasswordLink = async (email) => {
  try {
    const response = await fetch(`${config.baseAuthUrl}forgot-password`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email }),
    });

    if (!response.ok) {
      throw new Error("Failed to send reset password link.");
    }

    return await response.json();
  } catch (error) {
    console.error("Error sending reset password link:", error);
    throw error;
  }
};



export const getAdminDetails = async () => {
  try {
    const token = JSON.parse(localStorage.getItem("token"));
    // console.log("Retrieved token:", token);

    const response = await fetch(`${config.baseAuthUrl}admin-details`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error("Unauthorized access - please log in.");
      } else {
        throw new Error("Failed to fetch user details.");
      }
    }

    const data = await response.json();
    // console.log("User details fetched: ", data);

    // Store customerId and roles locally for use in subsequent API calls
    localStorage.setItem("customerId", data.customerId);
    localStorage.setItem("roles", JSON.stringify(data.roles)); // Save roles to localStorage

    return data;
  } catch (error) {
    console.error("Error fetching user details:", error);
    throw error;
  }
};

export const deleteUser = async (token, userId) => {
  try {
    const cleanToken = token?.replace(/^"|"$/g, "").trim();
    const response = await fetch(`${config.baseAuthUrl}delete-user/${userId}`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${cleanToken}`,
      },
    });

    if (!response.ok) {
      throw new Error("Failed to delete user");
    }

    return true; // Indicate success
  } catch (error) {
    console.error(`Error deleting user (ID: ${userId}):`, error);
    throw error;
  }
};
